import { actionCreator, mutationCreator } from 'Store/utils';
import FuturesApi from 'Apis/Futures';
import PlaceOrderRequest, { IPlaceOrderRequest } from 'Entities/futuresOrderRegistrar/PlaceOrderRequest';
import TradingApi from 'Apis/Trading';
import FuturesPositionsRequest from 'Entities/privatePresenter/FuturesPositionsRequest';
import { parsePaginationHeaders } from 'Lib/utils/PaginationParser';
import FuturesPosition, { IFuturesPosition } from 'Entities/privatePresenter/FuturesPosition';
import ApiError from 'Entities/ApiError';
import { SET_LOADING_OFF, SET_LOADING_ON } from 'Store/v2/Preloader';
import SwitchLeverageRequest from 'Entities/futuresOrderRegistrar/SwitchLeverageRequest';
import FuturesOrderPresenter from 'Entities/privatePresenter/FuturesOrderPresenter';
import FuturesTrade from 'Entities/privatePresenter/FuturesTrade';
import FuturesOrdersRequest from 'Entities/privatePresenter/FuturesOrdersRequest';
import FuturesTradesRequest from 'Entities/privatePresenter/FuturesTradesRequest';
import FuturesOrderRequest from 'Entities/privatePresenter/FuturesOrderRequest';
import FuturesTradeRequest from 'Entities/privatePresenter/FuturesTradeRequest';
import FuturesFundingRecordsRequest from 'Entities/privatePresenter/FuturesFundingRecordsRequest';
import FuturesFundingRecord from 'Entities/privatePresenter/FuturesFundingRecord';
import CancelOrderRequest from 'Entities/orderRegistrator/CancelOrderRequest';

export enum QUANTITY_TYPES {
    base = 'base',
    contract = 'contract',
}

export enum SUPPORTED_DIRECTIONS {
    long = 'LONG',
    short = 'SHORT',
}

enum SUPPORTED_ARRAY_NAMES {
    quickBalancesPositions = 'quickBalancesPositions',
    tablePositions = 'tablePositions',
}

export const resolveOrderType = (type: string): string => {
    switch (type) {
        case 'MARKET': {
            return 'Market';
        }
        case 'MARKET_STOP': {
            return 'Stop Market';
        }
        case 'LIMIT_STOP': {
            return 'Stop Limit';
        }
        case 'LIMIT': {
            return 'Limit';
        }
        case 'TAKE_PROFIT': {
            return 'Take Profit';
        }
        case 'STOP_LOSS': {
            return 'Stop Loss';
        }
        default: {
            return type
                .replace('_', ' ')
                .toLowerCase()
                .capitalize();
        }
    }
};

const state = {
    isSubscribedToPositionsUpdates: '',
    isSubscribedToQuickBalancesPositionsUpdates: false,
    isSubscribedToTablePositionsUpdates: false,
    isSubscribedToLeveragesUpdates: false,
    isSubscribedToOrdersUpdates: false,
    isSubscribedToTradesUpdates: false,
    isSubscribedToFundingsUpdates: false,

    availableLeverages: [] as number[],
    positions: [] as FuturesPosition[],
    quickBalancesPositions: [] as FuturesPosition[],
    tablePositions: [] as FuturesPosition[],
    orders: [] as FuturesOrderPresenter[],
    trades: [] as FuturesTrade[],
    fundings: [] as FuturesFundingRecord[],
    ui: {
        positionsCurrentPage: 1,
        ordersCurrentPage: 1,
        tradesCurrentPage: 1,
        fundingsCurrentPage: 1,
    },
};

export type FuturesState = typeof state;

const removeDuplicatedElements = (array: FuturesPosition[], arrayName: SUPPORTED_ARRAY_NAMES, state: FuturesState) => {
    const uniquePositions: string[] = [];
    const indexesToRemove: number[] = [];

    array.forEach(({ contractSymbol, side, accountId, placementName }, index) => {
        const positionIdentifier = `${contractSymbol}${side}${accountId}${placementName}`;
        if (uniquePositions.includes(positionIdentifier)) {
            indexesToRemove.push(index);
        } else {
            uniquePositions.push(positionIdentifier);
        }
    });

    indexesToRemove.forEach((positionIndex, arrayIndex) => {
        state[arrayName].splice(positionIndex - arrayIndex, 1);
    });
};
let accountPositionsSubscribtionId = null;
let assetPositionsSubscribtionIdawait = null;
export enum FuturesMutations {
    SET_POSITIONS = 'SET_POSITIONS',
    SET_QUICK_BALANCES_POSITIONS = 'SET_QUICK_BALANCES_POSITIONS',
    SET_AVAILABLE_LEVERAGES = 'SET_AVAILABLE_LEVERAGES',
    SET_TABLE_POSITIONS = 'SET_TABLE_POSITIONS',
    SET_CURRENT_POSITIONS_PAGE = 'SET_CURRENT_POSITIONS_PAGE',
    SET_CURRENT_ORDERS_PAGE = 'SET_CURRENT_ORDERS_PAGE',
    SET_CURRENT_TRADES_PAGE = 'SET_CURRENT_TRADES_PAGE',
    SET_CURRENT_FUNDINGS_PAGE = 'SET_CURRENT_FUNDINGS_PAGE',
    SET_ORDERS = 'SET_ORDERS',
    SET_TRADES = 'SET_TRADES',
    SET_FUNDINGS = 'SET_FUNDINGS',
}

export const SET_POSITIONS = mutationCreator<FuturesPosition[]>('Futures', FuturesMutations.SET_POSITIONS);
export const SET_QUICK_BALANCES_POSITIONS = mutationCreator<FuturesPosition[]>('Futures', FuturesMutations.SET_QUICK_BALANCES_POSITIONS);
export const SET_AVAILABLE_LEVERAGES = mutationCreator<number[]>('Futures', FuturesMutations.SET_AVAILABLE_LEVERAGES);
export const SET_TABLE_POSITIONS = mutationCreator<FuturesPosition[]>('Futures', FuturesMutations.SET_TABLE_POSITIONS);
export const SET_CURRENT_POSITIONS_PAGE = mutationCreator<number>('Futures', FuturesMutations.SET_CURRENT_POSITIONS_PAGE);
export const SET_CURRENT_ORDERS_PAGE = mutationCreator<number>('Futures', FuturesMutations.SET_CURRENT_ORDERS_PAGE);
export const SET_CURRENT_TRADES_PAGE = mutationCreator<number>('Futures', FuturesMutations.SET_CURRENT_TRADES_PAGE);
export const SET_CURRENT_FUNDINGS_PAGE = mutationCreator<number>('Futures', FuturesMutations.SET_CURRENT_FUNDINGS_PAGE);
export const SET_ORDERS = mutationCreator<FuturesOrderPresenter[]>('Futures', FuturesMutations.SET_ORDERS);
export const SET_TRADES = mutationCreator<FuturesTrade[]>('Futures', FuturesMutations.SET_TRADES);
export const SET_FUNDINGS = mutationCreator<FuturesFundingRecord[]>('Futures', FuturesMutations.SET_FUNDINGS);

const mutations: Record<FuturesMutations, (state: FuturesState, ...args: any) => void> = {
    SET_POSITIONS(state, { payload: positions }: ReturnType<typeof SET_POSITIONS>) {
        state.positions = positions;
    },
    SET_QUICK_BALANCES_POSITIONS(state, { payload: positions }: ReturnType<typeof SET_QUICK_BALANCES_POSITIONS>) {
        state.quickBalancesPositions = positions;
    },
    SET_AVAILABLE_LEVERAGES(state, { payload: leverages }: ReturnType<typeof SET_AVAILABLE_LEVERAGES>) {
        state.availableLeverages = leverages;
    },
    SET_TABLE_POSITIONS(state, { payload: positions }: ReturnType<typeof SET_TABLE_POSITIONS>) {
        state.tablePositions = positions;
    },
    SET_CURRENT_POSITIONS_PAGE(state, { payload: pageNumber }: ReturnType<typeof SET_CURRENT_POSITIONS_PAGE>) {
        state.ui.positionsCurrentPage = pageNumber;
    },
    SET_CURRENT_ORDERS_PAGE(state, { payload: pageNumber }: ReturnType<typeof SET_CURRENT_ORDERS_PAGE>) {
        state.ui.ordersCurrentPage = pageNumber;
    },
    SET_CURRENT_TRADES_PAGE(state, { payload: pageNumber }: ReturnType<typeof SET_CURRENT_TRADES_PAGE>) {
        state.ui.tradesCurrentPage = pageNumber;
    },
    SET_CURRENT_FUNDINGS_PAGE(state, { payload: pageNumber }: ReturnType<typeof SET_CURRENT_FUNDINGS_PAGE>) {
        state.ui.fundingsCurrentPage = pageNumber;
    },
    SET_ORDERS(state, { payload: orders }: ReturnType<typeof SET_ORDERS>) {
        state.orders = orders;
    },
    SET_TRADES(state, { payload: trades }: ReturnType<typeof SET_TRADES>) {
        state.trades = trades;
    },
    SET_FUNDINGS(state, { payload: fundings }: ReturnType<typeof SET_FUNDINGS>) {
        state.fundings = fundings;
    },
};

export enum FuturesActions {
    placeOrder = 'placeOrder',
    cancelOrder = 'cancelOrder',
    getPositions = 'getPositions',
    getQuickBalancesPositions = 'getQuickBalancesPositions',
    getAvailableLeverages = 'getAvailableLeverages',
    changeLeverage = 'changeLeverage',
    updatePosition = 'updatePosition',
    updateQuickBalancePosition = 'updateQuickBalancePosition',
    getTablePositions = 'getTablePositions',
    updateTablePosition = 'updateTablePosition',
    getOrders = 'getOrders',
    getTrades = 'getTrades',
    updateOrder = 'updateOrder',
    updateTrade = 'updateTrade',
    getFundings = 'getFundings',
}

export const placeOrder = actionCreator<IPlaceOrderRequest>('Futures', FuturesActions.placeOrder);
export const cancelOrder = actionCreator<string>('Futures', FuturesActions.cancelOrder);
export const getPositions = actionCreator</* placement */ string>('Futures', FuturesActions.getPositions);
export const getQuickBalancesPositions = actionCreator<undefined>('Futures', FuturesActions.getQuickBalancesPositions);
export const getAvailableLeverages = actionCreator<undefined>('Futures', FuturesActions.getAvailableLeverages);
export const changeLeverage = actionCreator<number>('Futures', FuturesActions.changeLeverage);
export const updatePosition = actionCreator<IFuturesPosition>('Futures', FuturesActions.updatePosition);
export const updateQuickBalancePosition = actionCreator<IFuturesPosition>('Futures', FuturesActions.updateQuickBalancePosition);
export const getTablePositions = actionCreator<undefined>('Futures', FuturesActions.getTablePositions);
export const updateTablePosition = actionCreator<IFuturesPosition>('Futures', FuturesActions.updateTablePosition);
export const getOrders = actionCreator<undefined>('Futures', FuturesActions.getOrders);
export const getTrades = actionCreator<undefined>('Futures', FuturesActions.getTrades);
export const updateOrder = actionCreator<FuturesOrderPresenter>('Futures', FuturesActions.updateOrder);
export const updateTrade = actionCreator<FuturesTrade>('Futures', FuturesActions.updateTrade);
export const getFundings = actionCreator<undefined>('Futures', FuturesActions.getFundings);

const actions = {
    async placeOrder({ dispatch, commit }, { payload: request }: ReturnType<typeof placeOrder>) {
        try {
            commit(SET_LOADING_ON(undefined), { root: true });
            const { data: order } = await FuturesApi.privateFuturesPlaceOrder(new PlaceOrderRequest(request));
            let tempOrder: any = order.serialize();
            tempOrder = { ...tempOrder, balanceId: '', initiatorId: '', initiatorType: '', orderType: request.type };
            await dispatch(updateOrder(tempOrder, true));
        } catch (error) {
            if (error instanceof ApiError) {
                await dispatch('Notificator/showErrorNotification', error.data ? error.data.message : 'Error during placing order', { root: true });
            }
        } finally {
            commit(SET_LOADING_OFF(undefined), { root: true });
        }
    },

    async cancelOrder({ dispatch }, { payload: id }: ReturnType<typeof cancelOrder>) {
        try {
            await FuturesApi.privateFuturesCancelOrder(new CancelOrderRequest({
                id,
            }));
        } catch (error) {
            if (error instanceof ApiError) {
                await dispatch(
                    'Notificator/showErrorNotification',
                    error.data ? error.data.message : 'Error cancelling TP/SL order',
                    { root: true },
                );
            }
        }
    },

    async getPositions({ state, dispatch, commit, rootGetters }, { payload: placement }: ReturnType<typeof getPositions>) {
        if (!state.isSubscribedToPositionsUpdates) {
            accountPositionsSubscribtionId = await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getPositions(placement, true));
                },
            }, { root: true });
            assetPositionsSubscribtionIdawait = dispatch('VuexEventListener/addActionListener', {
                type: 'Assets/setQuotationAsset',
                callback: async () => {
                    await dispatch(getPositions(placement, true));
                },
            }, { root: true });
            state.isSubscribedToPositionsUpdates = placement;
        } else if (state.isSubscribedToPositionsUpdates !== placement) {
            await dispatch('VuexEventListener/removeActionListener', {
                type: 'Accounts/setActiveAccount',
                id: accountPositionsSubscribtionId,
            }, { root: true });
            await dispatch('VuexEventListener/removeActionListener', {
                type: 'Assets/setQuotationAsset',
                id: assetPositionsSubscribtionIdawait,
            }, { root: true });
            accountPositionsSubscribtionId = await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getPositions(placement, true));
                },
            }, { root: true });
            assetPositionsSubscribtionIdawait = await dispatch('VuexEventListener/addActionListener', {
                type: 'Assets/setQuotationAsset',
                callback: async () => {
                    await dispatch(getPositions(placement, true));
                },
            }, { root: true });
        }
        if (!rootGetters['Accounts/activeAccountID'] || !rootGetters['Placements/activeTerminalPlacementName']) {
            return;
        }
        let allPositions: FuturesPosition[] = [];
        const { data: positions, headers } = await TradingApi.privateFuturesGetPositions(new FuturesPositionsRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            placementName: rootGetters['Placements/activeTerminalPlacementName'],
            quotationAsset: rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL'],
            includeTotal: true,
            page: 1,
            perPage: 100,
        }), true);
        allPositions = [...positions];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (typeof totalPages === 'number' && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraPositions } = await TradingApi.privateFuturesGetPositions(new FuturesPositionsRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        placementName: rootGetters['Placements/activeTerminalPlacementName'],
                        quotationAsset: rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL'],
                        page: i,
                        perPage: 100,
                    }));
                    allPositions = [...allPositions, ...extraPositions];
                }
            }
        }
        commit(SET_POSITIONS(allPositions, true));
    },
    async getQuickBalancesPositions({ state, dispatch, commit, rootGetters }) {
        if (!state.isSubscribedToQuickBalancesPositionsUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getQuickBalancesPositions(undefined, true));
                },
            }, { root: true });
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Balances/setAccountPortfolioActivePlacementId',
                callback: async () => {
                    await dispatch(getQuickBalancesPositions(undefined, true));
                },
            }, { root: true });
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Assets/setQuotationAsset',
                callback: async () => {
                    await dispatch(getQuickBalancesPositions(undefined, true));
                },
            }, { root: true });
            state.isSubscribedToQuickBalancesPositionsUpdates = true;
        }
        if (!rootGetters['Accounts/activeAccountID'] || !rootGetters['Balances/GET_ACCOUNT_PORTFOLIO_ACTIVE_PLACEMENT_ID']) {
            return;
        }
        let allPositions: FuturesPosition[] = [];
        const { data: positions, headers } = await TradingApi.privateFuturesGetPositions(new FuturesPositionsRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            placementName: rootGetters['Placements/getPlacementNameById'](rootGetters['Balances/GET_ACCOUNT_PORTFOLIO_ACTIVE_PLACEMENT_ID']),
            quotationAsset: rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL'],
            includeTotal: true,
            page: 1,
            perPage: 100,
        }), true);
        allPositions = [...positions];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (typeof totalPages === 'number' && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraPositions } = await TradingApi.privateFuturesGetPositions(new FuturesPositionsRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        placementName: rootGetters['Placements/getPlacementNameById'](rootGetters['Balances/GET_ACCOUNT_PORTFOLIO_ACTIVE_PLACEMENT_ID']),
                        quotationAsset: rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL'],
                        page: i,
                        perPage: 100,
                    }));
                    allPositions = [...allPositions, ...extraPositions];
                }
            }
        }
        commit(SET_QUICK_BALANCES_POSITIONS(allPositions, true));
    },
    async getAvailableLeverages({ state, commit, dispatch, rootGetters }) {
        if (!state.isSubscribedToLeveragesUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Placements/setActiveTerminalPlacementId',
                callback: async () => {
                    await dispatch(getAvailableLeverages(undefined, true));
                },
            }, { root: true });
            await dispatch('VuexEventListener/addActionListener', {
                type: 'AssetPairs/setActiveTerminalAssetPairId',
                callback: async () => {
                    await dispatch(getAvailableLeverages(undefined, true));
                },
            }, { root: true });
            state.isSubscribedToLeveragesUpdates = true;
        }

        const activeAssetPair = rootGetters['AssetPairs/GET_ACTIVE_TERMINAL_ASSET_PAIR'];
        if (!activeAssetPair) {
            return;
        }
        const leverages = activeAssetPair.placementLeverages;
        if (!leverages) {
            return;
        }
        commit(SET_AVAILABLE_LEVERAGES(leverages.map((l) => Number(l.level)).sort((a, b) => a - b), true));
    },
    async changeLeverage({ commit, dispatch, rootGetters }, { payload: leverage }: ReturnType<typeof changeLeverage>) {
        const accountId = rootGetters['Accounts/activeAccountID'];
        const placementName = rootGetters['Placements/activeTerminalPlacementName'];
        const contractSymbol = rootGetters['AssetPairs/GET_ACTIVE_TERMINAL_ASSET_PAIR_SYMBOL'];
        if (!accountId || !placementName || !contractSymbol || !leverage) {
            await dispatch('Notificator/showErrorNotification', 'Error during changing leverage, please try again later', { root: true });
            return;
        }

        try {
            commit(SET_LOADING_ON(undefined), { root: true });
            await FuturesApi.privateFuturesSwitchLeverage(new SwitchLeverageRequest({
                accountId,
                placementName,
                contractSymbol,
                leverage,
            }));
            await dispatch('Notificator/showSuccessNotification', 'Leverage has been successfully changed', { root: true });
        } catch (error) {
            if (error instanceof ApiError) {
                await dispatch('Notificator/showErrorNotification', error.data ? error.data.message : 'Error during changing leverage, please try again later', { root: true });
            }
        } finally {
            commit(SET_LOADING_OFF(undefined), { root: true });
        }
    },
    async updatePosition({ state, rootGetters }, { payload: position }: ReturnType<typeof updatePosition>) {
        const { positions } = state;
        const foundedPositionIndex = positions.findIndex((p) => p.contractSymbol === position.contractSymbol
                                            && p.accountId === position.accountId
                                            && p.placementName === position.placementName
                                            && p.side === position.side);
        if (foundedPositionIndex === -1) {
            if (rootGetters['Accounts/activeAccountID'] === position.accountId && rootGetters['Placements/activeTerminalPlacementName'] === position.placementName) {
                const { data: newPosition } = await TradingApi.privateFuturesGetPosition(new FuturesPositionsRequest({
                    placementName: position.placementName,
                    accountId: position.accountId,
                    contractSymbol: position.contractSymbol,
                    // eslint-disable-next-line no-nested-ternary
                    side: position.side === 'SHORT' ? 'SHORT' : position.side === 'LONG' ? 'LONG' : undefined,
                }));
                state.positions.unshift(newPosition);
            }
            return;
        }
        const tempPosition = positions[foundedPositionIndex].serialize();
        tempPosition.entryPrice = position.entryPrice;
        tempPosition.leverage = String(position.leverage);
        tempPosition.liquidationPrice = position.liquidationPrice;
        tempPosition.quantity = position.quantity;
        tempPosition.unrealizedProfit = position.unrealizedProfit;
        tempPosition.markPrice = position.markPrice;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (position.quantity !== 0 && position.quantity !== '0') {
            state.positions.splice(foundedPositionIndex, 1, new FuturesPosition(tempPosition));
        } else {
            state.positions.splice(foundedPositionIndex, 1);
        }
    },
    async updateQuickBalancePosition({ state, rootGetters }, { payload: position }: ReturnType<typeof updateQuickBalancePosition>) {
        const positions = state.quickBalancesPositions;
        const foundedPositionIndex = positions.findIndex((p) => p.contractSymbol === position.contractSymbol
            && p.accountId === position.accountId
            && p.placementName === position.placementName
            && p.side === position.side);
        if (foundedPositionIndex === -1) {
            if (rootGetters['Accounts/activeAccountID'] === position.accountId && rootGetters['Placements/getPlacementNameById'](rootGetters['Balances/GET_ACCOUNT_PORTFOLIO_ACTIVE_PLACEMENT_ID']) === position.placementName) {
                const { data: newPosition } = await TradingApi.privateFuturesGetPosition(new FuturesPositionsRequest({
                    placementName: position.placementName,
                    accountId: position.accountId,
                    contractSymbol: position.contractSymbol,
                    // eslint-disable-next-line no-nested-ternary
                    side: position.side === 'SHORT' ? 'SHORT' : position.side === 'LONG' ? 'LONG' : undefined,
                }));
                state.quickBalancesPositions.unshift(newPosition);
            }
            removeDuplicatedElements(state.quickBalancesPositions, SUPPORTED_ARRAY_NAMES.quickBalancesPositions, state);
            return;
        }
        const tempPosition = positions[foundedPositionIndex].serialize();
        tempPosition.entryPrice = position.entryPrice;
        tempPosition.leverage = String(position.leverage);
        tempPosition.liquidationPrice = position.liquidationPrice;
        tempPosition.quantity = position.quantity;
        tempPosition.unrealizedProfit = position.unrealizedProfit;
        tempPosition.profitPercent = position.profitPercent;
        tempPosition.quoteQuantity = position.quoteQuantity;
        tempPosition.markPrice = position.markPrice;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (position.quantity !== 0 && position.quantity !== '0') {
            state.quickBalancesPositions.splice(foundedPositionIndex, 1, new FuturesPosition(tempPosition));
        } else {
            state.quickBalancesPositions.splice(foundedPositionIndex, 1);
        }
        removeDuplicatedElements(state.quickBalancesPositions, SUPPORTED_ARRAY_NAMES.quickBalancesPositions, state);
    },
    async getTablePositions({ commit, dispatch, rootGetters }) {
        if (!state.isSubscribedToTablePositionsUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getTablePositions(undefined, true));
                },
            }, { root: true });
            state.isSubscribedToTablePositionsUpdates = true;
        }
        if (!rootGetters['Accounts/activeAccountID']) {
            return;
        }
        let allPositions: FuturesPosition[] = [];
        const { data: positions, headers } = await TradingApi.privateFuturesGetPositions(new FuturesPositionsRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            includeTotal: true,
            perPage: 100,
            page: 1,
        }), true);
        allPositions = [...positions];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (totalPages && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraPositions } = await TradingApi.privateFuturesGetPositions(new FuturesPositionsRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        perPage: 100,
                        page: i,
                    }));
                    allPositions = [...allPositions, ...extraPositions];
                }
            }
        }
        commit(SET_TABLE_POSITIONS(allPositions, true));
    },
    async updateTablePosition({ state, rootGetters }, { payload: position }: ReturnType<typeof updateTablePosition>) {
        const { tablePositions } = state;
        const foundedPositionIndex = tablePositions.findIndex((p) => p.contractSymbol === position.contractSymbol
            && p.accountId === position.accountId
            && p.placementName === position.placementName
            && p.side === position.side);
        if (foundedPositionIndex === -1) {
            if (rootGetters['Accounts/activeAccountID'] === position.accountId) {
                const { data: newPosition } = await TradingApi.privateFuturesGetPosition(new FuturesPositionsRequest({
                    placementName: position.placementName,
                    accountId: position.accountId,
                    contractSymbol: position.contractSymbol,
                    // eslint-disable-next-line no-nested-ternary
                    side: position.side === 'SHORT' ? 'SHORT' : position.side === 'LONG' ? 'LONG' : undefined,
                }));
                state.tablePositions.unshift(newPosition);
            }
            removeDuplicatedElements(state.tablePositions, SUPPORTED_ARRAY_NAMES.tablePositions, state);
            return;
        }
        const tempPosition = tablePositions[foundedPositionIndex].serialize();
        tempPosition.entryPrice = position.entryPrice;
        tempPosition.leverage = String(position.leverage);
        tempPosition.liquidationPrice = position.liquidationPrice;
        tempPosition.quantity = position.quantity;
        tempPosition.unrealizedProfit = position.unrealizedProfit;
        tempPosition.profitPercent = position.profitPercent;
        tempPosition.quoteQuantity = position.quoteQuantity;
        tempPosition.markPrice = position.markPrice;
        tempPosition.margin = position.margin;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (position.quantity !== 0 && position.quantity !== '0') {
            state.tablePositions.splice(foundedPositionIndex, 1, new FuturesPosition(tempPosition));
        } else {
            state.tablePositions.splice(foundedPositionIndex, 1);
        }
        removeDuplicatedElements(state.tablePositions, SUPPORTED_ARRAY_NAMES.tablePositions, state);
    },
    async getOrders({ commit, dispatch, rootGetters }) {
        if (!state.isSubscribedToOrdersUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getOrders(undefined, true));
                },
            }, { root: true });
            state.isSubscribedToOrdersUpdates = true;
        }

        if (!rootGetters['Accounts/activeAccountID']) {
            return;
        }
        let allOrders: FuturesOrderPresenter[] = [];
        const { data: orders, headers } = await TradingApi.privateFuturesGetOrders(new FuturesOrdersRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            page: 1,
            perPage: 100,
            includeTotal: true,
        }), true);
        allOrders = [...orders];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (totalPages && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraOrders } = await TradingApi.privateFuturesGetOrders(new FuturesOrdersRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        page: i,
                        perPage: 100,
                    }));
                    allOrders = [...allOrders, ...extraOrders];
                }
            }
        }
        commit(SET_ORDERS(allOrders, true));
    },
    async getTrades({ commit, dispatch, rootGetters }) {
        if (!state.isSubscribedToTradesUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getTrades(undefined, true));
                },
            }, { root: true });
            state.isSubscribedToTradesUpdates = true;
        }

        if (!rootGetters['Accounts/activeAccountID']) {
            return;
        }
        let allTrades: FuturesTrade[] = [];
        const { data: trades, headers } = await TradingApi.privateFuturesGetTrades(new FuturesTradesRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            page: 1,
            perPage: 100,
            includeTotal: true,
        }), true);
        allTrades = [...trades];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (totalPages && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraTrades } = await TradingApi.privateFuturesGetTrades(new FuturesTradesRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        page: i,
                        perPage: 100,
                    }));
                    allTrades = [...allTrades, ...extraTrades];
                }
            }
        }
        commit(SET_TRADES(allTrades, true));
    },
    async updateOrder({ state, dispatch, rootGetters }, { payload: order }: ReturnType<typeof updateOrder>) {
        const { orders } = state;
        const foundedOrderIndex = orders.findIndex(({ id }) => id === order.id);
        if (foundedOrderIndex === -1) {
            if (rootGetters['Accounts/activeAccountID'] === order.accountId) {
                const { data: newOrder } = await TradingApi.privateFuturesGetOrder(new FuturesOrderRequest({
                    id: order.id,
                }));
                let tempOrders = [...state.orders];
                tempOrders.unshift(newOrder);

                // remove duplicated orders
                const orderIdsSet = new Set<string>();
                tempOrders = tempOrders.filter(({ id }) => {
                    const flag = !orderIdsSet.has(id);
                    orderIdsSet.add(id);
                    return flag;
                });

                state.orders = tempOrders;
            }

            if (order.status?.toLowerCase() === 'rejected' || order.status?.toLowerCase() === 'canceled') {
                await dispatch('Notificator/showWarningNotification', `Order #${order.id} has been ${order.status}`, { root: true });
            } else {
                await dispatch('Notificator/showSuccessNotification', `Order #${order.id} has been ${order.status}`, { root: true });
            }

            return;
        }
        let tempOrder = orders[foundedOrderIndex].serialize();

        if (
            tempOrder.status === 'FILLED'
            || tempOrder.status === 'REJECTED'
            || tempOrder.status === 'CANCELED'
        ) {
            return;
        }

        if (order.status?.toLowerCase() !== tempOrder.status?.toLowerCase()) {
            if (order.status?.toLowerCase() === 'rejected' || order.status?.toLowerCase() === 'canceled') {
                await dispatch('Notificator/showWarningNotification', `Order #${order.id} has been ${order.status}`, { root: true });
            } else {
                await dispatch('Notificator/showSuccessNotification', `Order #${order.id} has been ${order.status}`, { root: true });
            }
        }

        tempOrder = { ...tempOrder, ...order };
        const tempOrders = [...state.orders];
        tempOrders.splice(foundedOrderIndex, 1, new FuturesOrderPresenter(tempOrder));
        state.orders = tempOrders;
    },
    async updateTrade({ state, rootGetters }, { payload: trade }: ReturnType<typeof updateTrade>) {
        const { trades } = state;
        const foundedTradeIndex = trades.findIndex(({ id }) => id === trade.id);
        if (foundedTradeIndex === -1) {
            if (rootGetters['Accounts/activeAccountID'] === trade.accountId) {
                const { data: newTrade } = await TradingApi.privateFuturesGetTrade(new FuturesTradeRequest({
                    id: trade.id,
                }));
                state.trades.unshift(newTrade);
            }
            return;
        }
        let tempTrade = trades[foundedTradeIndex].serialize();
        tempTrade = { ...tempTrade, ...trade };
        state.trades.splice(foundedTradeIndex, 1, new FuturesTrade(tempTrade));
    },
    async getFundings({ state, commit, dispatch, rootGetters }) {
        if (!state.isSubscribedToFundingsUpdates) {
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Accounts/setActiveAccount',
                callback: async () => {
                    await dispatch(getFundings(undefined, true));
                },
            }, { root: true });
            await dispatch('VuexEventListener/addActionListener', {
                type: 'Assets/setQuotationAsset',
                callback: async () => {
                    await dispatch(getFundings(undefined, true));
                },
            }, { root: true });
            state.isSubscribedToFundingsUpdates = true;
        }

        if (!rootGetters['Accounts/activeAccountID'] || !rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL']) {
            return;
        }
        let allFundings: FuturesFundingRecord[] = [];
        const { data: fundings, headers } = await TradingApi.privateFuturesGetFundingRecords(new FuturesFundingRecordsRequest({
            accountId: rootGetters['Accounts/activeAccountID'],
            quotationAsset: rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL'],
            includeTotal: true,
            page: 1,
            perPage: 100,
        }), true);
        allFundings = [...fundings];
        if (headers) {
            const totalPages = parsePaginationHeaders(headers).totalPage;
            if (totalPages && totalPages > 1) {
                for (let i = 2; i <= totalPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { data: extraFundings } = await TradingApi.privateFuturesGetFundingRecords(new FuturesFundingRecordsRequest({
                        accountId: rootGetters['Accounts/activeAccountID'],
                        quotationAsset: rootGetters['Assets/GET_QUOTATION_ASSET_SYMBOL'],
                        page: i,
                        perPage: 100,
                    }));
                    allFundings = [...allFundings, ...extraFundings];
                }
            }
        }
        commit(SET_FUNDINGS(allFundings, true));
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
